'use client';

import { useTranslations } from 'next-intl';
import React, { useCallback, useMemo, useState } from 'react';
import { BaseLineLoading2 } from '@/app/components/icons/BaseLineLoading2';
import { BaseLineNewcreator } from '@/app/components/icons/BaseLineNewcreator';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { Button } from '@/app/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/app/components/ui/dialog';
import { toast } from '@/app/components/ui/use-toast';
import { useFetch, useGlobalModal } from '@/app/hooks';
import { MAX_TREE_DEPTH } from '../../_constants';
import { useMoveFile } from '../../_hooks/use-move-file';
import { createFileItem, fileStateUpdaters } from '../../_utils';
import { fetchCreateFolder, fetchFolderTree } from '../../apis';
import type { BreadcrumbItem, FileItem, FolderTreeNode } from '../../types';
import { FolderTree } from './components/FolderTree';
import type { FolderData, RenameState, RenameStateData } from './types';
import { createRootFolder } from './utils';

const PAGE_SIZE = 15;

interface MoveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedItems: FileItem[];
  onConfirm: (successIds: string[]) => void;
  rootFolder?: BreadcrumbItem;
  currentFolderId?: string;
  onFilesUpdate?: (updater: (files: FileItem[]) => FileItem[]) => void;
}

// 自定义 Hook: 文件夹树数据管理
const useFolderTreeData = () => {
  const [folderDataMap, setFolderDataMap] = useState<Map<string, FolderData>>(new Map());

  // 更新文件夹数据
  const updateFolderData = useCallback((folderId: string, data: Partial<FolderData>) => {
    setFolderDataMap((prev) => {
      const newMap = new Map(prev);
      const existingData = newMap.get(folderId) || {
        children: [],
        loading: false,
        hasMore: true,
      };

      newMap.set(folderId, { ...existingData, ...data });
      return newMap;
    });
  }, []);

  // 通用的更新子节点函数
  const updateNodeData = (nodeId: string, updater: (node: FolderTreeNode) => FolderTreeNode) => {
    setFolderDataMap((prev) => {
      const newMap = new Map(prev);

      // 遍历所有父文件夹，找到包含该节点的文件夹并更新
      for (const [parentId, folderData] of Array.from(newMap.entries())) {
        if (folderData.children) {
          const updatedChildren = folderData.children.map((child: FolderTreeNode) =>
            child.id === nodeId ? updater(child) : child,
          );

          // 如果有变化，更新该父文件夹的数据
          const hasChanges = updatedChildren.some(
            (child: FolderTreeNode, index: number) => child !== folderData.children[index],
          );

          if (hasChanges) {
            newMap.set(parentId, { ...folderData, children: updatedChildren });
          }
        }
      }

      return newMap;
    });
  };

  // 更新文件夹的 has_sub_folder 状态
  const updateFolderHasSubFolder = (folderId: string, hasSubFolder: boolean) => {
    updateNodeData(folderId, (node) => ({
      ...node,
      folder_info: {
        ...node.folder_info,
        has_sub_folder: hasSubFolder,
      },
    }));
  };

  // 更新单个文件夹节点的名称
  const updateFolderNodeName = (nodeId: string, newName: string) => {
    updateNodeData(nodeId, (node) => ({
      ...node,
      name: newName,
    }));
  };

  // 重置状态
  const reset = () => {
    setFolderDataMap(new Map());
  };

  return {
    folderDataMap,
    updateFolderData,
    updateFolderHasSubFolder,
    updateFolderNodeName,
    reset,
  };
};

// 自定义 Hook: 文件夹数据加载
const useFolderDataLoader = (
  folderDataMap: Map<string, FolderData>,
  updateFolderData: (folderId: string, data: Partial<FolderData>) => void,
) => {
  const { run: loadFolderData, loading } = useFetch(
    (folderId: string, pageToken?: string) =>
      fetchFolderTree({
        query: {
          'pager.pagesz': PAGE_SIZE,
          'pager.page_token': pageToken,
          parent_id: folderId,
        },
      }),
    {
      manual: true,
      onSuccess: ({ data, error }, [folderId, pageToken]) => {
        if (error) {
          toast({
            message: error?.message,
            type: 'error',
          });
          updateFolderData(folderId, { loading: false });
          return;
        }

        if (data) {
          const existingData = folderDataMap.get(folderId);
          const existingChildren = existingData?.children || [];

          updateFolderData(folderId, {
            children: pageToken ? [...existingChildren, ...(data.nodes || [])] : data.nodes || [],
            nextPageToken: data.next_page_token,
            loading: false,
            hasMore: !!data.next_page_token && data.nodes.length === PAGE_SIZE,
          });
        }
      },
    },
  );

  const loadFolderChildren = useCallback(
    (folderId: string, pageToken?: string) => {
      const currentData = folderDataMap.get(folderId);
      // 如果正在加载，避免重复请求
      if (currentData?.loading) {
        return;
      }

      updateFolderData(folderId, { loading: true });
      loadFolderData(folderId, pageToken);
    },
    [folderDataMap, loadFolderData, updateFolderData],
  );

  return { loadFolderChildren, loading };
};

export const MoveDialog = ({
  open,
  onOpenChange,
  selectedItems,
  onConfirm,
  rootFolder: rootFolderBreadcrumb,
  currentFolderId,
  onFilesUpdate,
}: MoveDialogProps) => {
  const t = useTranslations();
  const rootFolder = useMemo(() => createRootFolder(rootFolderBreadcrumb), [rootFolderBreadcrumb]);
  const rootFolderId = rootFolder.id;
  const { open: openConfirmDialog } = useGlobalModal('confirmDialog');
  const [selectedFolder, setSelectedFolder] = useState<FolderTreeNode>(rootFolder);

  const { folderDataMap, updateFolderData, updateFolderHasSubFolder, updateFolderNodeName, reset } =
    useFolderTreeData();

  const { loadFolderChildren, loading } = useFolderDataLoader(folderDataMap, updateFolderData);

  // 重命名状态管理
  const [pendingMoveAction, setPendingMoveAction] = useState<(() => void) | null>(null);
  const [isRenaming, setIsRenaming] = useState(false);

  // 处理重命名状态变化
  const handleRenameStateChange = (state: RenameState, data: RenameStateData) => {
    setIsRenaming(state === 'renaming');

    // 如果重命名完成
    if (state === 'completed') {
      // 更新节点名称
      if (data.newName) {
        updateFolderNodeName(data.nodeId, data.newName);
      }

      // 如果有待执行的移动操作，现在执行它
      if (pendingMoveAction) {
        pendingMoveAction();
        setPendingMoveAction(null);
      }
    }
  };

  // 创建文件夹
  const { run: createFolder, loading: creatingFolder } = useFetch(
    () =>
      fetchCreateFolder({
        body: {
          parent_id: selectedFolder?.id || rootFolderId,
        },
      }),
    {
      manual: true,
      onSuccess: ({ data, error }) => {
        if (error) {
          if (error?.reason === 'DepthOverLimit') {
            openConfirmDialog({
              title: t('creations.moveDialog.levelLimit'),
              description: t('creations.moveDialog.levelLimitDescription', { limit: MAX_TREE_DEPTH }),
              primaryButtonText: t('common.dialog.gotIt'),
            });
          } else {
            toast({
              message: error?.message,
              type: 'error',
            });
          }
          return;
        }

        if (data?.folder) {
          const parentId = selectedFolder?.id || rootFolderId;

          // 更新父文件夹的 has_sub_folder 状态为 true
          updateFolderHasSubFolder(parentId, true);
          loadFolderChildren(parentId);

          // 选中新建的文件夹
          setSelectedFolder(data.folder);

          // 如果在当前目录下新建，需要更新列表
          if (parentId === currentFolderId) {
            const newFolderItem = createFileItem(data.folder);
            onFilesUpdate?.((files) => fileStateUpdaters.prependItems(files, [newFolderItem]));
          }
        }
      },
    },
  );

  // 移动文件
  const { moveFiles, movingFiles } = useMoveFile({
    onSuccess: (successIds: string[]) => {
      handleClose();
      onConfirm(successIds);
    },
  });

  // 检查是否达到最大深度
  const isMaxDepthReached = selectedFolder.depth >= MAX_TREE_DEPTH;

  // 初始化
  React.useEffect(() => {
    if (open) {
      // 重置选中状态为根文件夹
      const rootFolderNode = createRootFolder(rootFolder);
      setSelectedFolder(rootFolderNode);

      // 重置数据状态
      reset();

      // 加载根文件夹数据
      loadFolderChildren(rootFolderId);
    }
  }, [open, rootFolder]);

  const handleConfirmMove = () => {
    if (selectedItems.length > 0 && selectedFolder?.id) {
      const executeMoveAction = () => {
        moveFiles(selectedItems, selectedFolder.id);
      };

      // 如果有节点正在重命名，将移动操作推迟到重命名完成后执行
      if (isRenaming) {
        setPendingMoveAction(() => executeMoveAction);
      } else {
        // 立即执行移动操作
        executeMoveAction();
      }
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    reset();
    // 清理待执行的移动操作
    setPendingMoveAction(null);
  };

  if (!open || !rootFolder) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="h-fit max-h-fit min-h-fit w-[640px] gap-0 sm:gap-0 lg:gap-0">
        <DialogTitle>{t('creations.moveDialog.title')}</DialogTitle>
        <DialogDescription className="my-2">
          {selectedItems.length === 1
            ? t('creations.moveDialog.descriptionSingle', { count: selectedItems.length })
            : t('creations.moveDialog.description', { count: selectedItems.length })}
        </DialogDescription>

        <FolderTree
          rootFolderId={rootFolderId}
          rootFolderName={rootFolder.name}
          folderDataMap={folderDataMap}
          selectedFolder={selectedFolder}
          loading={loading}
          onNodeSelect={setSelectedFolder}
          loadFolderChildren={loadFolderChildren}
          onFilesUpdate={onFilesUpdate}
          onRenameStateChange={handleRenameStateChange}
        />

        <div className="mt-4 flex items-center justify-between">
          <CustomTooltip
            content={isMaxDepthReached && t('creations.moveDialog.levelLimitDescription', { limit: MAX_TREE_DEPTH })}
          >
            <Button
              className="flex items-center gap-2 px-0 text-sm hover:text-system-white80"
              onClick={createFolder}
              disabled={creatingFolder || isMaxDepthReached}
            >
              {creatingFolder ? (
                <BaseLineLoading2 className="animate-spin text-xl" />
              ) : (
                <BaseLineNewcreator className="text-xl" />
              )}
              {t('creations.folders.newFolder')}
            </Button>
          </CustomTooltip>
          <div className="space-x-4">
            <Button variant="outline" onClick={handleClose}>
              {t('creations.moveDialog.cancel')}
            </Button>
            <Button variant="primary" onClick={handleConfirmMove} disabled={movingFiles} loading={movingFiles}>
              {t('creations.moveDialog.confirm')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
